import { useRef, useState } from 'react'
import { useWorkspace } from '../store/AppContext'
import type { ImportDecision, ImportReport } from '../lib/api'

/**
 * Importing a Clockify detailed export.
 *
 * Preview first, always. The file decides which clients, projects and tasks
 * get created and whose hours these are, and none of that is visible from a
 * filename -- so nothing is written until the list of consequences has been
 * on screen.
 *
 * The one thing the importer cannot do is invent a person: a time entry needs
 * a profile, and a profile needs an account. Rows for somebody who has not
 * joined yet are held and attached later, rather than being quietly handed to
 * whoever happened to run the import.
 */
export function ImportPanel() {
  const { ws, act, flash } = useWorkspace()

  const fileInput = useRef<HTMLInputElement | null>(null)
  const [filename, setFilename] = useState('')
  const [csv, setCsv] = useState('')
  const [report, setReport] = useState<ImportReport | null>(null)
  const [mapping, setMapping] = useState<Record<string, ImportDecision>>({})
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<ImportReport | null>(null)

  const reset = () => {
    setFilename('')
    setCsv('')
    setReport(null)
    setMapping({})
    setDone(null)
    if (fileInput.current) fileInput.current.value = ''
  }

  const onPick = async (file: File | undefined) => {
    if (!file) return
    setDone(null)
    setBusy(true)
    try {
      const text = await file.text()
      setCsv(text)
      setFilename(file.name)
      const r = await act.importCsv({ csv: text, filename: file.name, mode: 'preview' })
      setReport(r)
      // Seed the picker from what the server decided, so an unmatched person
      // starts on "hold" rather than on nothing.
      const seed: Record<string, ImportDecision> = {}
      for (const p of r.people) if (!p.matched) seed[p.email] = 'park'
      setMapping(seed)
    } catch (err) {
      flash(err instanceof Error ? err.message : 'Could not read that file.')
      reset()
    } finally {
      setBusy(false)
    }
  }

  const repreview = async (next: Record<string, ImportDecision>) => {
    setMapping(next)
    if (!csv) return
    setBusy(true)
    try {
      setReport(await act.importCsv({ csv, filename, mode: 'preview', mapping: next }))
    } catch (err) {
      flash(err instanceof Error ? err.message : 'Could not refresh the preview.')
    } finally {
      setBusy(false)
    }
  }

  const commit = async () => {
    if (!csv) return
    setBusy(true)
    try {
      const r = await act.importCsv({ csv, filename, mode: 'commit', mapping })
      setDone(r)
      setReport(null)
      setCsv('')
      if (fileInput.current) fileInput.current.value = ''
      flash(`Imported ${r.imported} ${r.imported === 1 ? 'entry' : 'entries'}`)
    } catch (err) {
      flash(err instanceof Error ? err.message : 'The import failed.')
    } finally {
      setBusy(false)
    }
  }

  const newClients = report?.clients.filter((c) => !c.exists) ?? []
  const newProjects = report?.projects.filter((p) => !p.exists) ?? []

  return (
    <div className="card card--pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--mut)', letterSpacing: '.04em' }}>
          IMPORT
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--mut)' }}>A Clockify detailed export (.csv)</span>

        <label className="btn push" style={{ cursor: 'pointer', marginBottom: 0 }}>
          {busy && !report ? 'Reading…' : 'Choose file'}
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            disabled={busy}
            onChange={(e) => void onPick(e.target.files?.[0])}
          />
        </label>
      </div>

      {/* ---------- what happened ---------- */}
      {done && (
        <div
          style={{
            fontSize: 13,
            border: '1px solid var(--line)',
            borderRadius: 8,
            padding: '10px 12px',
          }}
        >
          <strong>Imported {done.imported}</strong>
          {done.created && (done.created.clients > 0 || done.created.projects > 0) && (
            <>
              {' '}
              · created {done.created.clients} clients, {done.created.projects} projects
            </>
          )}
          {done.parked > 0 && <> · {done.parked} held for people without an account</>}
          {done.duplicates > 0 && <> · {done.duplicates} already imported</>}
          {done.skipped > 0 && <> · {done.skipped} skipped</>}
          <button type="button" className="linkbtn" style={{ marginLeft: 10 }} onClick={reset}>
            Import another
          </button>
        </div>
      )}

      {/* ---------- the preview ---------- */}
      {report && (
        <>
          <div style={{ fontSize: 13 }}>
            <strong>{filename}</strong> — {report.rows} {report.rows === 1 ? 'row' : 'rows'}
            {report.currency && <> in {report.currency}</>}
          </div>

          {report.issues.length > 0 && (
            <details>
              <summary style={{ cursor: 'pointer', fontSize: 12.5, color: 'var(--dg)' }}>
                {report.issues.length} {report.issues.length === 1 ? 'row needs' : 'rows need'}{' '}
                attention
              </summary>
              <ul style={{ fontSize: 12.5, color: 'var(--mut)', margin: '6px 0 0', paddingLeft: 18 }}>
                {report.issues.slice(0, 25).map((i) => (
                  <li key={`${i.line}-${i.message}`}>
                    Line {i.line}: {i.message}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {(newClients.length > 0 || newProjects.length > 0) && (
            <div style={{ fontSize: 12.5, color: 'var(--mut)' }}>
              <strong style={{ color: 'var(--ink)' }}>Will create:</strong>{' '}
              {newClients.length > 0 && <>clients {newClients.map((c) => c.name).join(', ')}</>}
              {newClients.length > 0 && newProjects.length > 0 && ' · '}
              {newProjects.length > 0 && <>projects {newProjects.map((p) => p.name).join(', ')}</>}
            </div>
          )}

          {/* Whose time. An unmatched email is the one thing that must not be
              guessed, so each gets an explicit choice. */}
          {report.people.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {report.people.map((p) => (
                <div
                  key={p.email}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    alignItems: 'center',
                    fontSize: 13,
                  }}
                >
                  <span style={{ minWidth: 220 }}>
                    {p.email} <span style={{ color: 'var(--mut)' }}>({p.rows})</span>
                  </span>
                  {p.matched ? (
                    <span style={{ color: 'var(--gr)' }}>→ {p.matchedName}</span>
                  ) : (
                    <select
                      className="role-select"
                      aria-label={`Who is ${p.email}`}
                      value={String(mapping[p.email] ?? 'park')}
                      disabled={busy}
                      onChange={(e) =>
                        void repreview({ ...mapping, [p.email]: e.target.value as ImportDecision })
                      }
                    >
                      <option value="park">Hold until they join</option>
                      <option value="skip">Do not import</option>
                      {ws.team
                        .filter((m) => m.status === 'active')
                        .map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name}
                          </option>
                        ))}
                    </select>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 12.5, color: 'var(--mut)' }}>
              {report.imported} to import
              {report.duplicates > 0 && <> · {report.duplicates} already there</>}
              {report.parked > 0 && <> · {report.parked} held</>}
              {report.skipped > 0 && <> · {report.skipped} skipped</>}
            </span>
            <button type="button" className="btn push" onClick={reset} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void commit()}
              disabled={busy || report.imported + report.parked === 0}
            >
              {busy ? 'Working…' : `Import ${report.imported}`}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
